import { type FC, type MutableRefObject, useEffect, useMemo, useState } from 'react'

import { Skeleton } from 'antd'

import { Label } from 'common/components/Label/Label'
import { Show } from 'common/components/Show/Show'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'

import {
  useCreatePatientMedicalIssueMutation,
  useFetchAppointmentMedicalIssuesQuery,
  useFetchPatientMedicalIssuesQuery,
} from 'features/Schedule/api/medicalIssues.api'
import {
  getIssueErrorMessage,
  getIssueOptionLabel,
  getNotFoundMessage,
} from 'features/Schedule/api/medicalIssues.transform'
import type { MedicalIssue, MedicalIssueType } from 'features/Schedule/api/medicalIssues.types'

import { InlineCreateIssue } from './InlineCreateIssue'
import { IssueListBox } from './IssueListBox'
import styles from './medicalissues.module.scss'

const FIELD_LABEL = 'Medical Issue(s)'
const EMPTY_HINT = 'This patient has no open problems to choose from.'

export type MedicalIssuesFieldProps = {
  mode: 'create' | 'edit'
  siteId: string
  patientId?: number | null
  appointmentId?: number | null
  // Create: the selected issue ids; edit: only the newly selected (pending) ids. Read at save time.
  issueIdsRef: MutableRefObject<number[]>
  disabled?: boolean
}

export const MedicalIssuesField: FC<MedicalIssuesFieldProps> = ({
  mode,
  siteId,
  patientId,
  appointmentId,
  issueIdsRef,
  disabled = false,
}) => {
  const { setNotification } = useNotification()

  const hasPatient = !!patientId
  const patientIssuesQuery = useFetchPatientMedicalIssuesQuery(
    { patientId: patientId ?? 0 },
    { skip: !hasPatient || mode !== 'create' },
  )
  const [createIssue, { isLoading: isCreating }] = useCreatePatientMedicalIssueMutation()
  const [createdIssues, setCreatedIssues] = useState<MedicalIssue[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  useEffect(() => {
    setCreatedIssues([])
    setSelectedIds([])
  }, [patientId])

  const isEdit = mode === 'edit'
  const appointmentIssuesQuery = useFetchAppointmentMedicalIssuesQuery(
    { appointmentId: appointmentId ?? 0, siteId },
    { skip: !isEdit || !appointmentId || !siteId },
  )
  const appointmentIssues = appointmentIssuesQuery.data ?? []
  const serverLinkedIds = useMemo(
    () =>
      (appointmentIssuesQuery.data ?? [])
        .filter((issue) => issue.selected)
        .map((issue) => issue.id),
    [appointmentIssuesQuery.data],
  )

  // Edit: preselect the server-linked issues, keeping any pending picks across refetches.
  useEffect(() => {
    if (!isEdit || !appointmentIssuesQuery.data) return
    setSelectedIds((prev) => [
      ...serverLinkedIds,
      ...prev.filter((id) => !serverLinkedIds.includes(id)),
    ])
  }, [isEdit, appointmentIssuesQuery.data, serverLinkedIds])

  // The ref carries what save-time needs: all picks on create, only pending picks on edit.
  useEffect(() => {
    issueIdsRef.current = isEdit
      ? selectedIds.filter((id) => !serverLinkedIds.includes(id))
      : selectedIds
  }, [isEdit, selectedIds, serverLinkedIds, issueIdsRef])

  const patientIssues = patientIssuesQuery.data ?? []
  const queryError = isEdit
    ? appointmentIssuesQuery.error
    : hasPatient
    ? patientIssuesQuery.error
    : undefined
  const notFoundMessage = getNotFoundMessage(queryError)
  const hasBlockingError = !!queryError && !notFoundMessage

  useEffect(() => {
    if (hasBlockingError) {
      setNotification({
        title: 'Medical issues',
        description: 'Could not load the medical issues. Please try again.',
        type: ALERT_CONSTANTS.ERROR,
      })
    }
  }, [hasBlockingError, setNotification])

  // Freshly created problems are merged in until the active query refetches them.
  const listOptions = useMemo(() => {
    const baseIssues = isEdit ? appointmentIssues : patientIssues
    const knownIds = new Set(baseIssues.map((issue) => issue.id))
    const allIssues = [...baseIssues, ...createdIssues.filter((issue) => !knownIds.has(issue.id))]
    return allIssues.map((issue) => ({ value: issue.id, label: getIssueOptionLabel(issue) }))
  }, [isEdit, appointmentIssuesQuery.data, patientIssuesQuery.data, createdIssues])

  const handleInlineCreate = async (title: string, type: MedicalIssueType): Promise<boolean> => {
    try {
      const created = await createIssue({
        patientId: patientId as number,
        data: { title, type },
      }).unwrap()
      if (created) {
        setCreatedIssues((prev) => [...prev, created])
      }
      return true
    } catch (error) {
      setNotification({
        title: 'Medical issues',
        description: getIssueErrorMessage(error, 'Could not create the problem. Please try again.'),
        type: ALERT_CONSTANTS.ERROR,
      })
      return false
    }
  }

  if (hasBlockingError) return null

  if (notFoundMessage) {
    return (
      <div className={styles.parent}>
        <Label label={FIELD_LABEL} />
        <p className={styles.hint}>{notFoundMessage}</p>
      </div>
    )
  }

  const isListDisabled = isEdit ? disabled : disabled || !hasPatient
  const isListLoading = isEdit ? appointmentIssuesQuery.isLoading : patientIssuesQuery.isLoading
  const showEmptyHint = isEdit
    ? !isListLoading && !listOptions.length
    : hasPatient && !isListLoading && !listOptions.length

  const handleToggleIssue = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((issueId) => issueId !== id) : [...prev, id],
    )
  }

  return (
    <div className={styles.parent}>
      <Label label={FIELD_LABEL} />
      <Show
        when={!isListLoading}
        fallback={<Skeleton.Input active block className={styles.listBoxSkeleton} />}>
        <IssueListBox
          options={listOptions}
          selectedIds={selectedIds}
          ariaLabel={FIELD_LABEL}
          disabled={isListDisabled}
          onToggle={handleToggleIssue}
        />
        <Show when={showEmptyHint}>
          <p className={styles.hint}>{EMPTY_HINT}</p>
        </Show>
        <InlineCreateIssue
          hasPatient={hasPatient}
          disabled={disabled}
          isSubmitting={isCreating}
          onAdd={handleInlineCreate}
        />
      </Show>
    </div>
  )
}
